export type C1_FurnitureIconKind =
  | "armchair"
  | "bathtub"
  | "desk"
  | "dining-table-4"
  | "dining-table-6"
  | "double-bed"
  | "kitchen-block"
  | "base-cabinet"
  | "wall-cabinet"
  | "stove"
  | "sink"
  | "plant"
  | "refrigerator"
  | "oven"
  | "dishwasher"
  | "extractor-hood"
  | "shower"
  | "single-bed"
  | "sofa-2-seat"
  | "television"
  | "toilet"
  | "wardrobe"
  | "washbasin"
  | "washing-machine"
  | "stairs-straight"
  | "stairs-l"
  | "stairs-u";

type FurnitureIconProps = {
  kind: C1_FurnitureIconKind;
};

export type C1_KitchenApplianceIconKind = "refrigerator" | "oven" | "dishwasher";

export function C1_KitchenApplianceSymbol({ kind }: { kind: C1_KitchenApplianceIconKind }) {
  switch (kind) {
    case "refrigerator":
      return <><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M5 10h14M16 5v3M16 13v4" /></>;
    case "oven":
      return <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M4 8h16" /><circle cx="7" cy="5.5" r=".7" /><circle cx="10" cy="5.5" r=".7" /><rect x="7" y="11" width="10" height="7" rx="1" /><path d="M9 14h6" /></>;
    case "dishwasher":
      return <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M4 8h16M7 5.5h7M17 5.5h.01M7 17h10M8 17v-4M12 17v-6M16 17v-4" /></>;
  }
}

function DiningTableIcon({ chairs }: { chairs: 4 | 6 }) {
  const chairPositions = chairs === 4
    ? [[8, 3], [16, 3], [8, 21], [16, 21]]
    : [[6, 3], [12, 3], [18, 3], [6, 21], [12, 21], [18, 21]];

  return (
    <>
      <rect x="4" y="7" width="16" height="10" />
      {chairPositions.map(([x, y]) => <rect key={`${x}-${y}`} x={x - 2} y={y - 1.5} width="4" height="3" />)}
    </>
  );
}

export function C1_FurnitureIcon({ kind }: FurnitureIconProps) {
  let content;

  switch (kind) {
    case "armchair":
      content = (
        <>
          <rect x="6.5" y="6.5" width="11" height="13.5" rx="1" />
          <rect x="4" y="4" width="16" height="2.5" rx=".8" />
          <rect x="4" y="4" width="2.5" height="16" rx=".8" />
          <rect x="17.5" y="4" width="2.5" height="16" rx=".8" />
        </>
      );
      break;
    case "sofa-2-seat":
      content = (
        <>
          <rect x="5.5" y="9" width="13" height="8" rx="1" />
          <rect x="2" y="7" width="20" height="2" rx=".7" />
          <rect x="2" y="7" width="3.5" height="10" rx=".7" />
          <rect x="18.5" y="7" width="3.5" height="10" rx=".7" />
          <path d="M12 9v8" />
        </>
      );
      break;
    case "single-bed":
      content = <><rect x="6" y="2" width="12" height="20" /><rect x="8" y="4" width="8" height="5" /><path d="M6 11h12" /></>;
      break;
    case "double-bed":
      content = <><rect x="3" y="3" width="18" height="18" /><rect x="5" y="5" width="6" height="5" /><rect x="13" y="5" width="6" height="5" /><path d="M3 12h18M12 12v9" /></>;
      break;
    case "dining-table-4":
      content = <DiningTableIcon chairs={4} />;
      break;
    case "dining-table-6":
      content = <DiningTableIcon chairs={6} />;
      break;
    case "desk":
      content = <><rect x="3" y="5" width="18" height="14" strokeLinejoin="miter" /><rect x="7" y="8" width="10" height="6" rx="1" /><path d="M10 17h4M12 14v3" /><rect x="9" y="20.5" width="6" height="2.5" strokeLinejoin="miter" /></>;
      break;
    case "wardrobe":
      content = <><rect x="4" y="3" width="16" height="18" /><path d="M12 3v18M9.5 12h.01M14.5 12h.01" /></>;
      break;
    case "bathtub":
      content = (
        <>
          <path d="M2 11h20c-.8 4.8-2.9 8-6.4 8H8.4C4.9 19 2.8 15.8 2 11Z" />
          <path d="M19 11V7.8a1.3 1.3 0 0 0-2.6 0v.5" />
          <rect x="6.5" y="10.8" width="2.8" height="5" rx=".2" fill="#bdd0da" />
        </>
      );
      break;
    case "shower":
      content = (
        <>
          <rect x="3.5" y="3" width="17" height="18" fill="#dce6eb" fillOpacity=".35" />
          <path d="M16.5 5v16M18.7 10.7v3M10 3v2.8M8 7.3a2 1.5 0 0 1 4 0Z" />
          <path d="M8.2 9v5.5M10 9v5.5M11.8 9v5.5" stroke="#7d9aaa" strokeDasharray=".65 1.4" />
        </>
      );
      break;
    case "toilet":
      content = <><rect x="6" y="3" width="12" height="5" rx="2" /><path d="M7 10h10v3a5 5 0 0 1-10 0zM9 18h6v3H9z" /></>;
      break;
    case "washbasin":
      content = (
        <>
          <rect x="3" y="12" width="18" height="6.5" rx=".6" />
          <path d="M10.5 12V5.2h1.3V12M11.8 5.2h1.6M11.8 7.5h2.7a1 1 0 0 1 1 1v.3M10.8 15h2.4" />
        </>
      );
      break;
    case "kitchen-block":
      content = <><rect x="3" y="9" width="18" height="6" /><g strokeWidth=".8"><circle cx="16.5" cy="10.5" r=".9" /><circle cx="19.5" cy="10.5" r=".9" /><circle cx="16.5" cy="13.5" r=".9" /><circle cx="19.5" cy="13.5" r=".9" /></g></>;
      break;
    case "base-cabinet":
      content = <><path d="M3 5h18v16H3zM3 8h18M12 8v13M9.5 11h.01M14.5 11h.01M5 21v1M19 21v1" /></>;
      break;
    case "wall-cabinet":
      content = <><rect x="3" y="3" width="18" height="15" rx="1" /><path d="M12 3v15M9.5 11h.01M14.5 11h.01M6 21h12" /></>;
      break;
    case "stove":
      content = <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8" cy="8" r="2.5" /><circle cx="16" cy="8" r="3" /><circle cx="8" cy="16" r="3" /><circle cx="16" cy="16" r="2.5" /></>;
      break;
    case "sink":
      content = <><rect x="3" y="3" width="18" height="18" rx="2" /><rect x="6" y="7" width="12" height="11" rx="4" /><circle cx="12" cy="14" r="1" /><path d="M9 7V5h6v2M12 5V3" /></>;
      break;
    case "refrigerator":
    case "oven":
    case "dishwasher":
      content = <C1_KitchenApplianceSymbol kind={kind} />;
      break;
    case "extractor-hood":
      content = <><path d="M9 2h6v8l6 6H3l6-6V2ZM9 10h6M3 16v3h18v-3M7 22v-1M12 22v-1M17 22v-1" /><path d="M6 17.5h8M17 17.5h.01" /></>;
      break;
    case "washing-machine":
      content = <><rect x="4" y="3" width="16" height="18" rx="2" /><circle cx="12" cy="13" r="5" /><path d="M7 6h.01M10 6h5" /></>;
      break;
    case "television":
      content = <><rect x="3" y="5" width="18" height="13" rx="2" /><path d="M9 21h6M12 18v3M8 2l4 3 4-3" /></>;
      break;
    case "plant":
      content = <><path d="M12 15V7M12 10c-5 0-6-4-6-6 4 0 6 2 6 6zM12 12c5 0 6-4 6-6-4 0-6 2-6 6z" /><path d="M7 15h10l-1 6H8z" /></>;
      break;
    case "stairs-straight":
      content = <><rect x="6" y="2" width="12" height="20" /><path d="M6 6h12M6 10h12M6 14h12M6 18h12M12 20V5m0 0-3 3m3-3 3 3" /></>;
      break;
    case "stairs-l":
      content = <><path d="M5 3h9v12h7v6H5z" /><path d="M5 7h9M5 11h9M9 15v6M14 15v6" /></>;
      break;
    case "stairs-u":
      content = <><path d="M4 3h16v18h-6V9h-4v12H4z" /><path d="M4 7h6M14 7h6M4 11h6M14 11h6M4 15h6M14 15h6" /></>;
      break;
  }

  const squareCorners = ["double-bed", "single-bed", "wardrobe", "dining-table-4", "dining-table-6", "kitchen-block"].includes(kind);

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin={squareCorners ? "miter" : "round"} aria-hidden="true">
      {content}
    </svg>
  );
}
